// Perakende Mağaza Dashboard — kasiyer/mağaza personeli için tile-tabanlı ana ekran.
// Yetki bazlı: kullanıcının erişebildiği işlemlerin tile'ları gösterilir.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { fmtAmount } from '../utils/numberFormat';

const fmt = (n) => fmtAmount(n);

const TILE_GROUPS = [
  {
    title: 'SATIŞ',
    color: '#16a34a',
    tiles: [
      { key: 'retail-sales',    icon: '🛒', label: 'Perakende Satış',  shortcut: 'F1', perm: 'invoices.create', big: true },
      { key: 'retail-sales',    icon: '↩',  label: 'Perakende İade',   shortcut: 'F2', perm: 'invoices.create', mode: 'return' },
      { key: 'quotes-outgoing', icon: '📋', label: 'Verilen Teklif',   shortcut: 'F3', perm: 'invoices.view' },
    ],
  },
  {
    title: 'STOK / DEPO',
    color: '#3b82f6',
    tiles: [
      { key: 'warehouse-transfers', icon: '📦', label: 'Depo Nakil Fişi', perm: 'warehouse_transfer.create' },
      { key: 'stocks',              icon: '🔍', label: 'Stok Sorgu',       perm: 'stocks.view' },
    ],
  },
  {
    title: 'KASA / ÖDEME',
    color: '#d97706',
    tiles: [
      { key: 'cashregisters',     icon: '💰', label: 'Kasa Hareketleri', perm: 'cash.view' },
      { key: 'expense-vouchers',  icon: '💸', label: 'Gider Fişi',       perm: 'expense_voucher.create' },
      { key: 'cari-tahsilat',     icon: '📥', label: 'Cari Tahsilat',    perm: 'cari_receipts.create' },
      { key: 'cari-tediye',       icon: '📤', label: 'Cari Tediye',      perm: 'cari_payments.create' },
      { key: 'personnel-alacak',  icon: '👤', label: 'Personel Tediye',  perm: 'personnel.create' },
    ],
  },
  {
    title: 'MÜŞTERİ / RAPOR',
    color: '#7c3aed',
    tiles: [
      { key: 'accounts',          icon: '👥', label: 'Cari Listesi',     perm: 'accounts.view' },
      { key: 'reports-invoices',  icon: '📊', label: 'Satış Raporu',     perm: 'reports.view' },
      { key: 'reports-stocks',    icon: '📈', label: 'Stok Raporu',      perm: 'reports.view' },
    ],
  },
];

export default function RetailHubPage({ onNavigate, userName, canSee, assignedWarehouseId = null, assignedCashRegisterId = null }) {
  const _canSee = canSee || (() => true);
  const [assignedWarehouse, setAssignedWarehouse] = useState(null);
  const [assignedRegister, setAssignedRegister] = useState(null);
  const [stats, setStats] = useState({ todaySalesCount: 0, todaySalesTotal: 0, cashBalance: 0 });
  const [loading, setLoading] = useState(false);
  const [shiftStart] = useState(() => new Date());

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      // Bugünkü perakende satışlar
      const today = new Date().toISOString().slice(0, 10);
      const r = await api.get('/invoices', { params: { invoice_type: 'retail_sale', date_from: today, date_to: today, limit: 500 } }).catch(() => null);
      const items = r?.data?.data?.items || r?.data?.data || [];
      const total = items.reduce((s, i) => s + (parseFloat(i.grand_total) || 0), 0);

      // Kasa bakiyesi — atanmış kasa varsa onu, yoksa ilk aktif
      const ck = await api.get('/cash-registers').catch(() => null);
      const regs = ck?.data?.data?.items || ck?.data?.data || [];
      const myReg = assignedCashRegisterId ? regs.find(x => x.id === assignedCashRegisterId) : null;
      const activeReg = myReg || regs.find(x => x.is_active) || regs[0];
      setAssignedRegister(activeReg || null);
      const cashBal = parseFloat(activeReg?.current_balance) || 0;

      // Atanmış depo bilgisi
      if (assignedWarehouseId) {
        const wh = await api.get('/warehouses').catch(() => null);
        const list = wh?.data?.data?.items || wh?.data?.data || [];
        setAssignedWarehouse(list.find(x => x.id === assignedWarehouseId) || null);
      }

      setStats({ todaySalesCount: items.length, todaySalesTotal: total, cashBalance: cashBal });
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [assignedCashRegisterId, assignedWarehouseId]);

  useEffect(() => { fetchStats(); const t = setInterval(fetchStats, 30000); return () => clearInterval(t); }, [fetchStats]);

  // F1-F12 klavye kısayolları
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      const m = e.key.match(/^F(\d+)$/);
      if (!m) return;
      const idx = parseInt(m[1]) - 1;
      const allTiles = TILE_GROUPS.flatMap(g => g.tiles).filter(t => t.shortcut);
      const tile = allTiles[idx];
      if (tile && _canSee(tile.perm)) {
        e.preventDefault();
        onNavigate && onNavigate(tile.key);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onNavigate, _canSee]);

  const shiftElapsed = (() => {
    const ms = Date.now() - shiftStart.getTime();
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return `${h}sa ${m}dk`;
  })();

  return (
    <div style={{ padding: 16, maxWidth: 1400, margin: '0 auto' }}>
      {/* HEADER — vardiya özeti */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, alignItems: 'center',
        padding: '16px 20px', marginBottom: 20, borderRadius: 12,
        background: 'linear-gradient(135deg, rgba(59,130,246,0.08), rgba(124,58,237,0.04))',
        border: '1px solid var(--border-secondary)',
      }}>
        <div>
          <div style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: 4 }}>
            🏪 Perakende Mağaza
            {assignedWarehouse && <span style={{ marginLeft: 10, fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>· {assignedWarehouse.name}</span>}
          </div>
          <div style={{ fontSize: '0.85rem', color: 'var(--text-tertiary)' }}>
            👤 {userName || 'Kullanıcı'}
            {assignedRegister && <> · 💰 {assignedRegister.register_name}</>}
            {' · Vardiya: '}{shiftStart.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })} başladı ({shiftElapsed})
          </div>
        </div>
        <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
          <Kpi label="Bugün Satış" value={stats.todaySalesCount} unit="adet" color="#3b82f6" />
          <Kpi label="Bugün Ciro" value={fmt(stats.todaySalesTotal)} unit="₺" color="#16a34a" />
          <Kpi label="Kasa Bakiyesi" value={fmt(stats.cashBalance)} unit="₺" color="#d97706" />
          <button onClick={fetchStats} disabled={loading} title="Yenile" style={{
            background: 'transparent', border: '1px solid var(--border-secondary)', borderRadius: 8,
            padding: '8px 12px', cursor: 'pointer', fontSize: '0.85rem',
          }}>🔄</button>
        </div>
      </div>

      {/* TILE GROUPS */}
      {TILE_GROUPS.map(g => {
        const visibleTiles = g.tiles.filter(t => _canSee(t.perm));
        if (visibleTiles.length === 0) return null;
        return (
          <div key={g.title} style={{ marginBottom: 24 }}>
            <div style={{
              fontSize: '0.78rem', fontWeight: 700, color: g.color,
              letterSpacing: '0.08em', marginBottom: 10, paddingLeft: 4,
            }}>{g.title}</div>
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12,
            }}>
              {visibleTiles.map((t, i) => (
                <Tile key={`${t.key}-${i}`} tile={t} groupColor={g.color} onClick={() => onNavigate && onNavigate(t.key, t.mode)} />
              ))}
            </div>
          </div>
        );
      })}

      {/* INFO BAR */}
      <div style={{
        marginTop: 24, padding: '10px 16px', borderRadius: 8,
        background: 'var(--bg-secondary)', fontSize: '0.78rem', color: 'var(--text-tertiary)',
      }}>
        💡 <strong>Klavye kısayolları:</strong> F1 Satış · F2 İade · F3 Teklif · Tile'a tıklayarak ya da kısayollarla işlem başlatın.
      </div>
    </div>
  );
}

function Kpi({ label, value, unit, color }) {
  return (
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontSize: '0.68rem', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: '1.2rem', fontWeight: 700, color, fontFamily: 'monospace' }}>
        {value} <span style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', fontWeight: 400 }}>{unit}</span>
      </div>
    </div>
  );
}

function Tile({ tile, groupColor, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 8, padding: tile.big ? '24px 12px' : '18px 12px',
        minHeight: tile.big ? 140 : 110,
        background: hover ? groupColor + '15' : 'var(--bg-secondary)',
        border: '2px solid ' + (hover ? groupColor : 'var(--border-secondary)'),
        borderRadius: 12, cursor: 'pointer',
        transition: 'all 120ms',
        transform: hover ? 'translateY(-2px)' : 'none',
        boxShadow: hover ? `0 4px 12px ${groupColor}30` : 'none',
      }}
    >
      <div style={{ fontSize: tile.big ? '2.4rem' : '1.8rem' }}>{tile.icon}</div>
      <div style={{
        fontSize: tile.big ? '1rem' : '0.85rem', fontWeight: 600,
        color: hover ? groupColor : 'var(--text-primary)', textAlign: 'center',
      }}>
        {tile.label}
      </div>
      {tile.shortcut && (
        <div style={{
          position: 'absolute', top: 6, right: 8,
          fontSize: '0.65rem', fontFamily: 'monospace',
          color: 'var(--text-tertiary)', background: 'var(--bg-primary)',
          padding: '2px 6px', borderRadius: 4, border: '1px solid var(--border-secondary)',
        }}>{tile.shortcut}</div>
      )}
    </button>
  );
}
